import { AlertTriangle, ExternalLink, FileSignature, FolderOpen } from 'lucide-react'
import {
  Card, ProgressBar, StatTile, cx,
} from '@/components/ui/primitives'
import { CONTRACT, GATES, PROJECT, weekOn } from '@/data/project'
import { RISKS, computeRag } from '@/data/risks'
import { useAuth } from '@/lib/auth'
import type { UrlState } from '@/lib/urlState'
import { useDecisions } from '@/features/decisions/useDecisions'
import { isOverdue } from '@/features/decisions/types'
import { useOwners } from '@/features/owners/useOwners'
import { OWNERS_DUE, isNamed, isSettled } from '@/features/owners/types'
import { useTasks } from '@/features/roadmap/useRoadmap'
import { ExportMarkdownButton } from '@/components/ui/ExportButton'
import { InFlightCard, WaitingOnYouCard } from './ActionPanels'
import { inFlight, waitingOnClient } from './actionItems'
import { statusSnapshot } from './statusSnapshot'
import { daysUntil, useProjectSummary } from './useProjectSummary'

const RAG_STYLES = {
  Green: { dot: 'bg-mc-done', text: 'text-mc-done', ring: 'ring-mc-done/30', label: 'On track' },
  Amber: { dot: 'bg-mc-accent', text: 'text-mc-high', ring: 'ring-mc-accent/40', label: 'Watch' },
  Red:   { dot: 'bg-mc-critical', text: 'text-mc-critical', ring: 'ring-mc-critical/30', label: 'At risk' },
} as const

export function HomePage({ onNavigate }: { onNavigate: (next: Partial<UrlState>) => void }) {
  const { isAdmin, can } = useAuth()
  const { summary, live } = useProjectSummary()
  const { decisions } = useDecisions()
  const { owners } = useOwners()
  const { tasks } = useTasks(can('roadmap.view'))

  // Roles with no confirmed owner. Everything else on this page queues behind these, so
  // they go above the fold rather than one tab away.
  const ownersOutstanding = owners.filter((o) => !isSettled(o))
  const ownersDueIn = daysUntil(OWNERS_DUE)

  const openDecisions = decisions.filter((d) => d.status !== 'Decided')
  const criticalOpen = openDecisions.filter((d) => d.priority === 'Critical')
  const overdueCritical = criticalOpen.filter((d) => isOverdue(d))

  const gatesAtRisk = GATES.filter((g) => summary.gateStatus[g.id] === 'At Risk').length
  const openCriticalRisks = RISKS.filter((r) => r.severity === 'Critical' && r.status === 'Open').length

  const rag = computeRag({
    openCriticalRisks,
    overdueCriticalDecisions: overdueCritical.length,
    gatesAtRisk,
  })
  const style = RAG_STYLES[rag.flag]

  const today = new Date()
  const days = daysUntil(PROJECT.goLive)
  const pct = (summary.hoursLogged / PROJECT.baselineHours) * 100
  const week = weekOn(today)

  // A register the reader cannot open contributes nothing, rather than rows that would
  // lead to a tab they do not have.
  const waiting = waitingOnClient({
    owners: can('owners.view') ? owners : [],
    decisions: can('decisions.view') ? decisions : [],
    tasks,
    today,
  })
  const flight = inFlight(tasks, today)

  return (
    <div className="space-y-4">
      {can('owners.view') && ownersOutstanding.length > 0 && (
        <div className="rounded-lg border border-mc-critical/30 bg-mc-critical-soft px-4 py-3">
          <p className="flex flex-wrap items-center gap-2 text-sm font-bold text-mc-critical">
            <AlertTriangle size={16} aria-hidden />
            We are waiting on {ownersOutstanding.length} project{' '}
            {ownersOutstanding.length === 1 ? 'owner' : 'owners'}
            <span className="font-normal">
              {ownersDueIn > 0 ? `— due in ${ownersDueIn} days`
                : ownersDueIn === 0 ? '— due today'
                : `— ${Math.abs(ownersDueIn)} days past due`}
            </span>
          </p>
          <p className="mt-1 text-sm text-mc-critical">
            Nothing else can start until these names exist. Open the Owners tab to fill them in.
          </p>
          <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
            {ownersOutstanding.map((o) => (
              <li key={o.id} className="text-sm text-mc-critical">
                {o.role} — {isNamed(o) ? `${o.name}, not yet confirmed` : 'nobody named'}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Go-live and overall flag — the two things anyone opens this page for. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-xs font-bold tracking-wide text-mc-muted uppercase">Go-live</p>
              <p className="mt-1 text-3xl font-bold text-mc-primary sm:text-4xl">
                {new Date(`${PROJECT.goLive}T00:00:00`).toLocaleDateString(undefined, {
                  month: 'long', day: 'numeric', year: 'numeric',
                })}
              </p>
              <p className="mt-1 text-sm text-mc-muted">
                {days > 0 ? `${days} days away` : days === 0 ? 'Today' : `${Math.abs(days)} days ago`}
                {' · '}hypercare through {new Date(`${PROJECT.hypercareEnds}T00:00:00`)
                  .toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
              </p>
            </div>
            <div className={cx('rounded-lg bg-mc-surface-2 px-4 py-3 ring-1 ring-inset', style.ring)}>
              <p className="text-xs font-bold tracking-wide text-mc-muted uppercase">Status</p>
              <p className={cx('mt-1 flex items-center gap-2 text-xl font-bold', style.text)}>
                <span className={cx('h-3 w-3 rounded-full', style.dot)} aria-hidden />
                {style.label}
              </p>
              <ul className="mt-1.5 space-y-0.5 text-xs text-mc-muted">
                {rag.reasons.map((r) => <li key={r}>{r}</li>)}
              </ul>
            </div>
          </div>
        </Card>

        <Card title="Project documents">
          <div className="space-y-2">
            <a
              href={PROJECT.driveFolderUrl}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 rounded border border-mc-line px-3 py-2.5 text-sm font-medium text-mc-primary hover:bg-mc-surface-2"
            >
              <FolderOpen size={16} aria-hidden />
              Shared Drive folder
              <ExternalLink size={13} className="ml-auto text-mc-muted" aria-hidden />
            </a>
            {can('contract.view') && (
              <a
                href={CONTRACT.url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 rounded border border-mc-line px-3 py-2.5 text-sm font-medium text-mc-primary hover:bg-mc-surface-2"
              >
                <FileSignature size={16} aria-hidden />
                Signed contract
                <ExternalLink size={13} className="ml-auto text-mc-muted" aria-hidden />
              </a>
            )}
          </div>
        </Card>
      </div>

      {/* Numbers. */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile
          label="Hours logged"
          value={summary.hoursLogged.toFixed(1)}
          unit={`of ${PROJECT.baselineHours}`}
          hint={`${pct.toFixed(1)}% of the planning estimate`}
        />
        <StatTile
          label="Open decisions"
          value={openDecisions.length}
          tone={criticalOpen.length > 0 ? 'critical' : 'neutral'}
          hint={`${criticalOpen.length} critical`}
        />
        <StatTile
          label="Past target"
          value={openDecisions.filter((d) => isOverdue(d)).length}
          tone={overdueCritical.length > 0 ? 'critical' : 'done'}
          hint="Decisions needing an answer"
        />
        <StatTile
          label="Current week"
          value={week.id}
          hint={week.dates}
        />
      </div>

      {isAdmin && (
        <Card title="Burn — Milestone only" subtitle="Not shown to client accounts">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <StatTile
              label="Billed value"
              value={`$${(summary.hoursLogged * PROJECT.rate).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
              hint={`at $${PROJECT.rate}/hr`}
            />
            <StatTile
              label="Baseline fee"
              value={`$${PROJECT.baselineFee.toLocaleString()}`}
              hint={`${PROJECT.baselineHours} hrs planning estimate`}
            />
            <StatTile
              label="Remaining"
              value={`${(PROJECT.baselineHours - summary.hoursLogged).toFixed(1)}`}
              unit="hrs"
              hint="Against the estimate, not a cap"
            />
          </div>
        </Card>
      )}

      <Card
        title="Progress"
        subtitle={live ? undefined : 'Showing the last published figures'}
        action={
          <ExportMarkdownButton
            label="status snapshot"
            build={() => statusSnapshot({
              takenOn: new Date(),
              goLive: PROJECT.goLive,
              daysToGoLive: days,
              currentWeek: week.id,
              flag: rag.flag,
              hoursLogged: summary.hoursLogged,
              baselineHours: PROJECT.baselineHours,
              openDecisions: openDecisions.length,
              criticalOpen: criticalOpen.length,
              ownersOutstanding: can('owners.view')
                ? ownersOutstanding.map((o) => o.role)
                : [],
              gatesAtRisk,
              // Milestone only, exactly as the Burn card is. A client account exports the
              // page it can see, not the page somebody else can.
              burn: isAdmin
                ? {
                    billed: summary.hoursLogged * PROJECT.rate,
                    rate: PROJECT.rate,
                    remaining: PROJECT.baselineHours - summary.hoursLogged,
                  }
                : null,
            })}
          >
            Export status
          </ExportMarkdownButton>
        }
      >
        <ProgressBar
          value={summary.hoursLogged}
          max={PROJECT.baselineHours}
          label={`${summary.hoursLogged.toFixed(1)} of ${PROJECT.baselineHours} hours`}
        />
        <p className="mt-3 text-xs text-mc-muted">
          Hours are a planning estimate on a time-and-materials engagement, not a budget cap.
          Percentage of hours is not the same as percentage of scope delivered.
        </p>
      </Card>

      {/* What is waiting on the client leads: it is the half of the board they can move. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <WaitingOnYouCard items={waiting} today={today} onNavigate={onNavigate} />
        {can('roadmap.view') && (
          <InFlightCard
            active={flight.active} next={flight.next} today={today} onNavigate={onNavigate}
          />
        )}
      </div>
    </div>
  )
}
